import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatRut, formatPhone } from '@/utils/formatters';

export default function Suppliers() {
  const { API } = useContext(AuthContext);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, formData);
      toast.success('Proveedor creado exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', rut: '', email: '', phone: '', address: '' });
      loadSuppliers();
    } catch (error) {
      toast.error('Error al crear proveedor');
    }
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="suppliers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Proveedores</h1>
          <p className="text-muted-foreground">Administra tus proveedores</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-supplier-button">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Proveedor</DialogTitle>
              <DialogDescription>Registra un nuevo proveedor</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre o Razón Social *</Label>
                <Input
                  data-testid="supplier-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>RUT *</Label>
                <Input
                  data-testid="supplier-rut-input"
                  value={formData.rut}
                  onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                  placeholder="12.345.678-9"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="supplier-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  data-testid="supplier-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  data-testid="supplier-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <Button type="submit" data-testid="submit-supplier-button" className="w-full">
                Crear Proveedor
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {suppliers.map((supplier) => (
          <Card key={supplier.id} className="hover:shadow-md transition-all" data-testid={`supplier-card-${supplier.id}`}>
            <CardHeader>
              <CardTitle className="text-lg">{supplier.name}</CardTitle>
              <CardDescription>RUT: {supplier.rut}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {supplier.email && <p><strong>Email:</strong> {supplier.email}</p>}
              {supplier.phone && <p><strong>Teléfono:</strong> {supplier.phone}</p>}
              {supplier.address && <p><strong>Dirección:</strong> {supplier.address}</p>}
              <div className="pt-2 border-t">
                <p className="font-mono"><strong>Saldo:</strong> ${(supplier.balance ?? 0).toFixed(2)}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {suppliers.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">No hay proveedores registrados</p>
            <p className="text-sm text-muted-foreground">Crea tu primer proveedor para comenzar</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}